import type { BaseIconName } from "@repo/ui/components/icon";
import { DefaultChatTransport } from "ai";
import type { ComponentType } from "react";

import {
  AskUserToolSubtitle,
  BlockMutateSubtitle,
  BlockToolSubtitle,
  BoardByIdSubtitle,
  BoardFilterMutateSubtitle,
  BoardFilterToolSubtitle,
  BoardMutateSubtitle,
  BoardToolSubtitle,
  CreateBoardSubtitle,
  CreateChartSubtitle,
  DatasetByIdSubtitle,
  DatasetFromOutputSubtitle,
  DatasetMutateSubtitle,
  DatasetToolSubtitle,
  DatasourceMutateSubtitle,
  DatasourceToolSubtitle,
  getBlockMutateTitle,
  getBlockToolTitle,
  getBoardFilterMutateTitle,
  getBoardFilterToolTitle,
  getBoardMutateTitle,
  getBoardToolTitle,
  getDatasetMutateTitle,
  getDatasetToolTitle,
  getDatasourceMutateTitle,
  getDatasourceToolTitle,
  InputTitleSubtitle,
  RunQuerySubtitle,
} from "./tool-subtitles";

/** Props passed to every tool subtitle component. */
export type ToolSubtitleProps = { input: unknown; output: unknown };

type ToolLabel = {
  title: string;
  icon?: BaseIconName;
  Subtitle?: ComponentType<ToolSubtitleProps>;
  getTitle?: (args: ToolSubtitleProps) => string | undefined;
};

export const TOOL_LABELS: Record<string, ToolLabel> = {
  "tool-runQueryTool": { title: "Run query", Subtitle: RunQuerySubtitle },
  "tool-createChartTool": {
    title: "Create chart",
    icon: "data-line-chart",
    Subtitle: CreateChartSubtitle,
  },
  "tool-saveRunToBoardTool": { title: "Send to board", icon: "dashboard" },
  "tool-workflow-updateDatasetQueryWorkflow": {
    title: "Update query",
    icon: "command-line",
  },
  "tool-searchCatalogTool": {
    title: "Catalog insights",
    icon: "magnifying-glass",
  },
  "tool-datasourceTool": {
    title: "Datasource",
    icon: "stack-rounded",
    Subtitle: DatasourceToolSubtitle,
    getTitle: getDatasourceToolTitle,
  },
  "tool-datasourceMutateTool": {
    title: "Datasource",
    icon: "stack-rounded",
    Subtitle: DatasourceMutateSubtitle,
    getTitle: getDatasourceMutateTitle,
  },
  "tool-readKnowledgeTool": {
    title: "Read knowledge",
    icon: "magnifying-glass",
  },
  "tool-saveInsightTool": {
    title: "Save insight",
  },
  "tool-askUserTool": {
    title: "Question for you",
    icon: "comment",
    Subtitle: AskUserToolSubtitle,
  },
  "tool-presentPlanTool": {
    title: "Plan",
    icon: "clipboard",
  },
  "tool-blockTool": {
    title: "Block",
    icon: "eye",
    Subtitle: BlockToolSubtitle,
    getTitle: getBlockToolTitle,
  },
  "tool-blockMutateTool": {
    title: "Block",
    icon: "blocks-plus",
    Subtitle: BlockMutateSubtitle,
    getTitle: getBlockMutateTitle,
  },
  "tool-boardTool": {
    title: "Board",
    icon: "dashboard",
    Subtitle: BoardToolSubtitle,
    getTitle: getBoardToolTitle,
  },
  "tool-boardMutateTool": {
    title: "Board",
    icon: "dashboard",
    Subtitle: BoardMutateSubtitle,
    getTitle: getBoardMutateTitle,
  },
  "tool-datasetTool": {
    title: "Dataset",
    icon: "dataset",
    Subtitle: DatasetToolSubtitle,
    getTitle: getDatasetToolTitle,
  },
  "tool-datasetMutateTool": {
    title: "Dataset",
    icon: "dataset-plus",
    Subtitle: DatasetMutateSubtitle,
    getTitle: getDatasetMutateTitle,
  },
  "tool-boardFilterTool": {
    title: "Filter",
    icon: "filter",
    Subtitle: BoardFilterToolSubtitle,
    getTitle: getBoardFilterToolTitle,
  },
  "tool-boardFilterMutateTool": {
    title: "Filter",
    icon: "filter",
    Subtitle: BoardFilterMutateSubtitle,
    getTitle: getBoardFilterMutateTitle,
  },
  "tool-readCurrentQueryTool": {
    title: "Read query",
    icon: "command-line",
  },
  // Legacy entries kept so historical chat transcripts (which carry the old
  // tool ids in persisted parts) still render with the right title/subtitle.
  // The supervisor + exploration agents no longer expose these tools.
  "tool-createDatasetTool": {
    title: "Create dataset",
    icon: "dataset-plus",
    Subtitle: InputTitleSubtitle,
  },
  "tool-updateDatasetTool": {
    title: "Update dataset",
    icon: "dataset",
    Subtitle: DatasetFromOutputSubtitle,
  },
  "tool-updateDatasetQueryTool": {
    title: "Update query",
    icon: "command-line",
  },
  "tool-listOrganizationDatasetsTool": {
    title: "List datasets",
    icon: "dataset",
  },
  "tool-readDatasetQueryTool": {
    title: "Read dataset",
    icon: "dataset",
    Subtitle: DatasetByIdSubtitle,
  },
  "tool-createBoardTool": {
    title: "Create board",
    icon: "dashboard",
    Subtitle: CreateBoardSubtitle,
  },
  "tool-listBoardsTool": {
    title: "List boards",
    icon: "dashboard",
  },
  "tool-readBoardTool": {
    title: "Read board",
    icon: "dashboard",
    Subtitle: BoardByIdSubtitle,
  },
  "tool-updateBoardTool": {
    title: "Update board",
    icon: "dashboard",
    Subtitle: BoardByIdSubtitle,
  },
  "tool-deleteBoardTool": {
    title: "Delete board",
    icon: "dashboard",
    Subtitle: BoardByIdSubtitle,
  },
  "tool-duplicateBoardTool": {
    title: "Duplicate board",
    icon: "duplicate",
    Subtitle: BoardByIdSubtitle,
  },
  "tool-createBoardFilterTool": {
    title: "Create filter",
    icon: "filter",
  },
  "tool-listBoardFiltersTool": {
    title: "List filters",
    icon: "filter",
  },
  "tool-updateBoardFilterTool": {
    title: "Update filter",
    icon: "filter",
  },
  "tool-deleteBoardFilterTool": {
    title: "Delete filter",
    icon: "filter",
  },
  "tool-listOrganizationDatasourcesTool": {
    title: "List datasources",
    icon: "stack-rounded",
  },
  "tool-readDatasourceTool": {
    title: "Read datasource",
    icon: "stack-rounded",
  },
  "tool-updateDatasourceTool": {
    title: "Update datasource",
    icon: "stack-rounded",
  },
};

export const MAX_IMAGES_PER_MESSAGE = 5;

/**
 * How many times a single turn may auto-continue after ending on
 * `finishReason: "tool-calls"` (the supervisor's `maxSteps` budget was
 * exhausted mid tool-loop). Each continuation is a fresh bounded server
 * segment resumed from Mastra memory, so this caps a turn at roughly
 * `maxSteps * (MAX_AUTO_CONTINUATIONS + 1)` effective steps before the
 * user-facing {@link STEP_LIMIT_NOTICE} is shown.
 */
export const MAX_AUTO_CONTINUATIONS = 3;

export {
  CONTINUATION_PROMPT,
  CONTINUATION_SENTINEL,
} from "@repo/shared/types/chat-continuation";

/**
 * Session-only notice shown once {@link MAX_AUTO_CONTINUATIONS} is exhausted
 * and the turn still wants more steps. Not persisted (Mastra owns the
 * transcript), so it clears on reload.
 */
export const STEP_LIMIT_NOTICE =
  "I reached my step limit before finishing this turn. Send a message to have me continue.";

const AGENT_API = `${import.meta.env.VITE_DOMAIN_API}/api/mastra/agents/supervisor-agent`;

export const transport = new DefaultChatTransport({
  api: AGENT_API,
  credentials: "include",
  // Mastra owns the transcript: the server loads thread history from memory and
  // is the single writer that persists every turn. Per Mastra's recommended
  // pattern we send only the newest message (plus the per-call `body` —
  // threadId, requestContext, and any tool-resume payload), never the full
  // client transcript. Sending the whole history would double-write against
  // Mastra's own persistence and cause message-ordering bugs. The AI SDK's own
  // fields (`id`, `trigger`, `messageId`) are forwarded so regenerate keeps
  // working. On a tool-resume (`sendMessage(undefined, { body: { resumeData,
  // runId } })`) the trailing element is the pending assistant message, which
  // `handleChatStream` ignores on the resume path (it uses `resumeData`/`runId`).
  prepareSendMessagesRequest: ({ messages, body, id, trigger, messageId }) => ({
    body: {
      ...body,
      id,
      trigger,
      messageId,
      messages: messages.slice(-1),
    },
  }),
});
